import { useState, useCallback } from "react";
import {
  Row,
  Col,
  Card,
  CardTitle,
  CardSubtitle,
  Form,
  Label,
  Input,
} from "reactstrap";
import {
  HandleUserNameChange,
  HandleStudentClassChange,
  HandleUserEmailChange,
  CheckIfEmailIsOccupied,
  SaveStudentFocusControlSetting,
  DeleteStudentFocusControlSetting,
} from "../../actions/teacherMutations";
import {
  GetCoursesByStudentEmail,
  GetCoursesWithResultsByStudentId,
} from "../../actions/teacherQueries";
import ButtonSendActivationMail from "../Button/ButtonSendActivationMail";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";
import FinalExamUnlockerContainer from "./FinalExamUnlockerContainer";
import Select from "react-select";
import { PacmanLoader } from "react-spinners";
import { FormattedMessage, useIntl } from "react-intl";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { checkNameLengthAndCharacters } from "../../Utilities/checkerFunctions";
import { formatServiceError } from "../../Utilities/serviceErrorHelper";
import ModuleLocker from "./ModuleLocker";
import FocusControl from "../Teacher/FocusControl";
import useFocusControl from "./hooks/useFocusControl";

export default function StudentAdministration(props) {
  const intl = useIntl();
  const queryClient = useQueryClient();

  const [updatingStudent, setUpdatingStudent] = useState(false);
  const [showCourses, setShowCourses] = useState(false);
  const [emailOccupied, setEmailOccupied] = useState(false);
  const [invalidEmail, setInvalidEmail] = useState(false);
  const mutateStudentName = useMutation({ mutationFn: HandleUserNameChange });
  const mutateStudentClass = useMutation({ mutationFn: HandleStudentClassChange });
  const mutateStudentEmail = useMutation({ mutationFn: HandleUserEmailChange });
  const mutateCheckStudentEmail = useMutation({ mutationFn: CheckIfEmailIsOccupied });

  const studentCourses = GetCoursesByStudentEmail(
    props.studentHasLicense ? props.student.email : null
  );
  const studentCoursesDataWithResult = GetCoursesWithResultsByStudentId(
    props.student.id
  );

  const onRefreshSettings = useCallback(async () => {
    const studentId = String(props.studentId ?? props.student.id);

    await queryClient.refetchQueries({
      queryKey: ["FetchStudentInfoByStudentId", studentId],
    });
    const updatedStudent = queryClient.getQueryData([
      "FetchStudentInfoByStudentId",
      studentId,
    ]);
    return updatedStudent?.focusControlSettings || [];
  }, [props.student.id, props.studentId, queryClient]);

  const fc = useFocusControl({
    settingKey: "studentSetting",
    entityId: props.student.id,
    entityIdKey: "studentId",
    focusControlSettings: props.student.focusControlSettings,
    saveFn: SaveStudentFocusControlSetting,
    deleteFn: DeleteStudentFocusControlSetting,
    onRefreshSettings,
  });

  if (studentCourses.isPending || studentCoursesDataWithResult.isPending) {
    return (
      <div className="inline-pacman-loader">
        <PacmanLoader color="#20a8d8" size={20} />
      </div>
    );
  }

  let values = {
    student: props.student,
    oldEmail: props.student.email,
    newEmail: props.student.email,
    oldName: props.student.name,
    newName: props.student.name,
    class: {
      name: props.student.class.name,
      id: props.student.class.id,
      schoolId: props.student.class.schoolId,
    },
  };

  const handleChangedStudentName = (event) => {
    if (event.target.value !== props.student.name) {
      const nameCheck = checkNameLengthAndCharacters(event.target.value);
      if (nameCheck !== true) {
        toast.warn(nameCheck);
      } else {
        values.oldName = props.student.name;
        let studentData = props.student;
        studentData.name = event.target.value;
        queryClient.setQueryData(["GetStudentInfoByStudentId"], studentData);

        setUpdatingStudent(true);
        mutateStudentName.mutate(
          {
            oldEmail: values.oldEmail,
            newEmail: values.oldEmail,
            newName: event.target.value,
          },
          {
            onSuccess: () => {
              queryClient.invalidateQueries({ queryKey: ["FetchTeacherInformation"] });
              setUpdatingStudent(false);
              toast.success(<FormattedMessage id="StudentUpdated" />);
            },
            onError: (err) => {
              studentData.name = values.oldName;
              queryClient.setQueryData(
                ["GetStudentInfoByStudentId"],
                studentData
              );
              setUpdatingStudent(false);
              toast.warn(formatServiceError(intl, err?.response?.data));
            },
          }
        );
      }
    }
  };

  const handleEmailWarningChange = () => {
    if (invalidEmail) setInvalidEmail(false);
    if (emailOccupied) setEmailOccupied(false);
  };

  const checkEmailOnChange = (event) => {
    if (
      !/^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9-]+(?:\.[a-zA-Z0-9-]+)*$/.test(
        event.target.value
      )
    ) {
      setInvalidEmail(true);
    } else {
      setInvalidEmail(false);
      if (event.target.value === values.oldEmail) {
        setEmailOccupied(false);
        return;
      }
      mutateCheckStudentEmail.mutate(
        {
          oldEmail: values.oldEmail,
          newEmail:
            event.target.name === "email" &&
            event.target.value !== props.student.email
              ? event.target.value
              : values.newEmail,
          studentName:
            event.target.name === "name" &&
            event.target.value !== props.student.name
              ? event.target.value
              : values.newName,
        },
        {
          onSuccess: (data) => {
            if (data.data === true) {
              setEmailOccupied(true);
            } else {
              setEmailOccupied(false);
              handleChangedStudentEmail(event);
            }
          },
          onError: (err) => {
            setUpdatingStudent(false);
            toast.warn(formatServiceError(intl, err?.response?.data));
          },
        }
      );
    }
  };

  const handleChangedStudentEmail = (event) => {
    let studentData = props.student;
    studentData.email = event.target.value;
    queryClient.setQueryData(["GetStudentInfoByStudentId"], studentData);

    setUpdatingStudent(true);
    mutateStudentEmail.mutate(
      {
        oldEmail: values.oldEmail,
        newEmail:
          event.target.name === "email" ? event.target.value : values.oldEmail,
        userName:
          event.target.name === "name" &&
          event.target.value !== props.student.name
            ? event.target.value
            : values.newName,
      },
      {
        onSuccess: () => {
          setUpdatingStudent(false);
          toast.success(<FormattedMessage id="StudentUpdated" />);
          queryClient.invalidateQueries({ queryKey: ["FetchTeacherInformation"] });
          queryClient.invalidateQueries({ queryKey: [
            "GetCoursesWithResultsByStudentId",
            props.student.id,
          ] });
        },
        onError: (err) => {
          let studentData = props.student;
          studentData.email = values.oldEmail;
          queryClient.setQueryData(["GetStudentInfoByStudentId"], studentData);
          setUpdatingStudent(false);
          toast.warn(formatServiceError(intl, err?.response?.data));
        },
      }
    );
  };

  const handleChangedStudentClass = (value) => {
    setUpdatingStudent(true);
    mutateStudentClass.mutate(
      {
        id: props.student.id,
        name: props.student.name,
        class: {
          name: value.value.name,
          id: value.value.id,
          schoolId: props.student.class.schoolId,
        },
      },
      {
        onSuccess: () => {
          queryClient.invalidateQueries({ queryKey: ["FetchTeacherInformation"] });

          setUpdatingStudent(false);
          toast.success(<FormattedMessage id="StudentUpdated" />);
        },
        onError: (err) => {
          setUpdatingStudent(false);
          toast.warn(formatServiceError(intl, err?.response?.data));
        },
      }
    );
  };

  if (props.school) {
    if (!props.student.class)
      props.student.class = {
        id: 0,
        name: "",
      };
    props.student.class.schoolId = props.school.id;
  }

  var teacherClasses = [];
  props.teacherInfo.programmes.forEach((program) => {
    program.classes.forEach((cl) => {
      teacherClasses.push(cl);
    });
  });

  const toggleCoursesDropdown = () => {
    setShowCourses((prevstate) => !prevstate);
  };

  const searchList = () => {
    return studentCourses.data.map((course, i) => (
      <Row key={i}>
        <Col>{course.name}</Col>
      </Row>
    ));
  };

  const classSelectOptions = [];
  teacherClasses.forEach((cl) => {
    let cls = { value: cl, label: cl.name };
    classSelectOptions.push(cls);
  });

  const currentClassOption = { value: "", label: "" };
  if (props.student.class.name != null) {
    currentClassOption.value = props.student.class.name;
    currentClassOption.label = props.student.class.name;
  }

  return (
    <div className="animated fadeIn">
      <Col lg={12}>
        <Card body>
          <CardTitle>
            <FormattedMessage id="StudentInformation" />
            {updatingStudent ? (
              <div style={{ float: "right" }}>
                {" "}
                <div style={{ marginTop: "-11%", marginBottom: "22%" }}>
                  <PacmanLoader color={"#20a8d8"} size={15} loading={true} />
                </div>
                <FormattedMessage id="Updating" />
              </div>
            ) : (
              ""
            )}
            <hr />
          </CardTitle>
          <CardSubtitle>
            <Row>
              <Col lg={8}></Col>
              <Col lg={4}>
                <ButtonSendActivationMail
                  user={props.student}
                  printButtonText={true}
                />
              </Col>
            </Row>
            <Row>
              <Col sm="6">
                <Form action="" method="post">
                  <Label htmlFor="nf-name" className="form-label">
                    <FormattedMessage id="Name" />:
                  </Label>
                  <Input
                    type="text"
                    id="name"
                    name="name"
                    onBlur={handleChangedStudentName}
                    defaultValue={props.student.name}
                    placeholder={props.student.name}
                    autoComplete="false"
                  />
                  <Label htmlFor="email" className="form-label">
                    <FormattedMessage id="Email" />:
                  </Label>
                  {emailOccupied ? (
                    <span className="ms-2 text-danger">
                      <FontAwesomeIcon icon="exclamation" className="me-1" />
                      <FormattedMessage id="EmailOccupied" />
                    </span>
                  ) : (
                    ""
                  )}
                  {invalidEmail ? (
                    <span className="ms-2 text-danger">
                      <FontAwesomeIcon icon="exclamation" className="me-1" />
                      <FormattedMessage id="WrongEmailFormat" />
                    </span>
                  ) : (
                    ""
                  )}
                  <Input
                    type="email"
                    id="email"
                    name="email"
                    onBlur={checkEmailOnChange}
                    onChange={handleEmailWarningChange}
                    placeholder={props.student.email}
                    defaultValue={props.student.email}
                    autoComplete="false"
                  />
                  <Label htmlFor="classAddTeacher" className="form-label">
                    <FormattedMessage id="Class" />:
                  </Label>
                  <Select
                    disabled={!props.fullAccessTeacher}
                    name="class"
                    menuPortalTarget={document.body}
                    menuPosition={"fixed"}
                    onChange={handleChangedStudentClass}
                    options={classSelectOptions}
                    defaultValue={currentClassOption}
                  />
                </Form>
              </Col>
            </Row>
          </CardSubtitle>
        </Card>
      </Col>

      {props.studentHasLicense && (
        <>
          <FocusControl
            targetSettings={fc.targetSettings}
            onDelete={fc.handleDeleteFocusControl}
            deletingTarget={fc.deletingTarget}
            inheritedSourceId="InheritedFromClassOrProgram"
            onSaveTarget={fc.handleSaveFocusTarget}
            savingTarget={fc.savingTarget}
          />

          <ModuleLocker
            courses={studentCoursesDataWithResult.data}
            level={"Student"}
            currentLevelId={props.student.id}
            students={[props.student.id]}
          />
        </>
      )}

      <FinalExamUnlockerContainer
        programId={props.student.class.programId}
        courses={studentCourses.data}
        searchList={searchList}
        placeHolder={props.placeHolder}
        toggleCoursesDropdown={toggleCoursesDropdown}
        showCourses={showCourses}
        studentCoursesWithStudentModule={studentCoursesDataWithResult}
        studentEmail={props.student.email}
        level="Student"
        studentHasNoLicense={!props.studentHasLicense}
        student={props.student}
        classId={props.student.class.id}
      />
    </div>
  );
}
